import React, { useState } from 'react';
import styled from '@emotion/native';
import { View } from 'react-native';
import { useQuery } from '@apollo/client';
import { ActivityIndicator } from 'react-native-paper';
import { format } from 'date-fns';
import { GET_ADMIN_BOOKINGS } from '@/shared/queries/admin';
import {
  AdminBookingFilter,
  QueryAdminBookingsArgs,
  QueryResult,
} from '@/shared/types/generated';
import { LoraText } from '@/shared/components/StyledText';
import { green, sand } from '@/shared/styles/colors';
import {
  EmptyState,
  ErrorState,
  Eyebrow,
  FilterChips,
  Panel,
  Pill,
  RowCard,
  Small,
  Spread,
  Stack,
  Strong,
} from './AdminUI';
import {
  bookingStatus,
  localZoneName,
  paymentSummary,
  sessionLabel,
  toDate,
} from './format';

const durationLabel = (minutes: number) =>
  minutes >= 60 && minutes % 60 === 0 ? `${minutes / 60} hr` : `${minutes} min`;

// The server returns at most this many
const BOOKINGS_LIMIT = 100;

const EMPTY: Record<AdminBookingFilter, { title: string; body: string }> = {
  [AdminBookingFilter.NeedsReply]: {
    title: 'No requests waiting',
    body: 'Every in-person request has been accepted or declined.',
  },
  [AdminBookingFilter.Upcoming]: {
    title: 'Nothing booked',
    body: 'No confirmed sessions coming up.',
  },
  [AdminBookingFilter.Recent]: {
    title: 'No past sessions',
    body: 'Sessions show up here once their start time has passed.',
  },
};

const BookingsPanel = ({
  isDesktop,
  initialFilter,
}: {
  isDesktop: boolean;
  initialFilter?: string;
}) => {
  const [filter, setFilter] = useState<AdminBookingFilter>(
    initialFilter === 'needs-reply'
      ? AdminBookingFilter.NeedsReply
      : AdminBookingFilter.Upcoming,
  );
  const { data, loading, error, refetch } = useQuery<
    QueryResult<'adminBookings'>,
    QueryAdminBookingsArgs
  >(GET_ADMIN_BOOKINGS, {
    variables: { filter },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const bookings = data?.adminBookings ?? [];
  const thisYear = new Date().getFullYear();

  return (
    <View>
      <Spread>
        <FilterChips
          selected={filter}
          onSelect={setFilter}
          options={[
            { value: AdminBookingFilter.NeedsReply, label: 'Needs reply' },
            { value: AdminBookingFilter.Upcoming, label: 'Upcoming' },
            { value: AdminBookingFilter.Recent, label: 'Past' },
          ]}
        />
        <Small>
          Times in {localZoneName()}
          {bookings.length >= BOOKINGS_LIMIT
            ? ` · showing the ${filter === AdminBookingFilter.Recent ? 'latest' : 'next'} ${BOOKINGS_LIMIT}`
            : ''}
        </Small>
      </Spread>

      {loading && !data ? (
        <ActivityIndicator
          size="large"
          color={green}
          style={{ marginTop: 80 }}
        />
      ) : error && !data ? (
        <ErrorState what="bookings" onRetry={() => refetch()} />
      ) : bookings.length === 0 ? (
        <EmptyState {...EMPTY[filter]} />
      ) : (
        <Panel>
          {bookings.map((booking) => {
            const start = toDate(booking.startTime);
            const status = bookingStatus(booking);
            return (
              <RowCard key={booking.id} isDesktop={isDesktop}>
                <DateBlock>
                  <Eyebrow>
                    {format(
                      start,
                      start.getFullYear() === thisYear ? 'EEE' : 'EEE yyyy',
                    )}
                  </Eyebrow>
                  <DateText>{format(start, 'MMM d')}</DateText>
                  <Small>{format(start, 'h:mm a')}</Small>
                </DateBlock>

                <Stack style={{ flex: 3 }}>
                  <Strong>
                    {booking.clientName} with {booking.stylistName}
                  </Strong>
                  <Small>
                    {sessionLabel(booking)} ·{' '}
                    {durationLabel(booking.estimatedDuration)} · booking{' '}
                    {booking.id}
                  </Small>
                </Stack>

                <Stack style={{ flex: 2 }}>
                  <View
                    style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}
                  >
                    <Pill text={status.label} tone={status.tone} />
                    {/* Left out of the overview numbers */}
                    {booking.isTest ? (
                      <Pill text="Test" tone="neutral" />
                    ) : null}
                  </View>
                  {booking.discountCode ? (
                    <Small>
                      {booking.discountCode} · {booking.discountPercentage}% off
                    </Small>
                  ) : null}
                </Stack>

                <Stack
                  style={{
                    flex: 2,
                    alignItems: isDesktop ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Strong>{paymentSummary(booking)}</Strong>
                </Stack>
              </RowCard>
            );
          })}
        </Panel>
      )}
    </View>
  );
};

const DateBlock = styled.View({
  width: 84,
  backgroundColor: sand,
  borderRadius: 8,
  paddingVertical: 10,
  alignItems: 'center',
  gap: 2,
});

const DateText = styled(LoraText)({
  fontSize: 20,
  color: green,
  textTransform: 'uppercase',
});

export default BookingsPanel;
